import { QueryClient } from '@tanstack/react-query';
import { broadcastProfileSwitch } from './authV2TabSync';

/**
 * Auth v2 — client-state reset utility (AB#1436).
 *
 * Invoked whenever the active profile changes (login with auto-selected profile,
 * profile switch, chooser-selection after login). Guarantees that no data from
 * the previous profile leaks into the new profile's session on the client.
 *
 * Server-side tenant isolation (EF query filters + `CurrentUserService`) is
 * already protected by the new access-token claims. This utility handles the
 * browser-side equivalent: React Query cache, auth tokens in localStorage,
 * and broadcasts a `devanza:profile-switched` DOM event so every React context
 * provider that caches profile-scoped data can listen and clear.
 *
 * The SignalR connection is torn down by the NotificationsContext listening to
 * the same event — the provider sees the new token in localStorage and
 * re-establishes the hub with it.
 *
 * Cross-tab sync (AB#1439) lives alongside this utility: the BroadcastChannel
 * message handler calls this function on the receiving tab.
 */
export interface ProfileSwitchContext {
    /** New access token to store in localStorage before resetting caches. */
    readonly newAccessToken: string;
    /** Optional new refresh token (e.g., after login). Left untouched on a pure switch. */
    readonly newRefreshToken?: string;
    /** Target profile id after the switch. Emitted in the event detail. */
    readonly activeProfileId: number;
    /** Source of the switch — for logging and telemetry. */
    readonly source: 'login' | 'switch-profile' | 'tab-broadcast' | 'revocation-fallback';
}

/**
 * The DOM event name emitted on every successful profile change.
 * Contexts that cache profile-scoped data (UserContext, UserProfileContext,
 * BusinessContext, OnboardingContext, DashboardFilterContext,
 * NotificationsContext) MUST listen for this and clear their state.
 */
export const PROFILE_SWITCHED_EVENT = 'devanza:profile-switched';

export interface ProfileSwitchedEventDetail {
    readonly activeProfileId: number;
    readonly newAccessToken: string;
    readonly source: ProfileSwitchContext['source'];
}

/**
 * Reset every layer of client state that caches profile-scoped data.
 *
 * Order matters:
 *   1. Write the new token(s) first so listeners that refetch see the new auth.
 *   2. Clear the React Query cache — forces all active queries to refetch.
 *   3. Dispatch the DOM event — contexts clear their local state synchronously.
 *
 * This function is intentionally synchronous-looking (returns void) — the
 * async work (refetches, SignalR reconnect) happens in the listeners.
 */
export function resetClientStateForProfileSwitch(
    queryClient: QueryClient,
    context: ProfileSwitchContext,
): void {
    // 1. Persist new token(s). Every subsequent HTTP request via the axios
    //    interceptor in `src/services/api.ts` will attach the new token.
    localStorage.setItem('accessToken', context.newAccessToken);
    if (context.newRefreshToken) {
        localStorage.setItem('refreshToken', context.newRefreshToken);
    }

    // 2. Clear React Query cache. This cancels in-flight queries and drops
    //    cached responses — next render will refetch with the new token and
    //    the new profile's tenant scope (enforced server-side).
    queryClient.clear();

    // 3. Notify every context provider so they can reset their own caches.
    //    Using a DOM event (rather than requiring each context to import this
    //    utility) keeps the dependency graph acyclic and lets the
    //    NotificationsContext reconnect SignalR without this file importing it.
    const detail: ProfileSwitchedEventDetail = {
        activeProfileId: context.activeProfileId,
        newAccessToken: context.newAccessToken,
        source: context.source,
    };
    window.dispatchEvent(new CustomEvent<ProfileSwitchedEventDetail>(PROFILE_SWITCHED_EVENT, { detail }));

    // 4. AB#1439 — cross-tab sync. Broadcast to other tabs, but never from
    //    the `tab-broadcast` source (that would loop forever).
    if (context.source !== 'tab-broadcast') {
        broadcastProfileSwitch(context.activeProfileId, context.newAccessToken);
    }
}

/**
 * Helper for contexts to subscribe to the profile-switched event.
 * Returns a cleanup function suitable for `useEffect`.
 *
 * @example
 * useEffect(() => subscribeToProfileSwitch(detail => {
 *   setCurrentBusiness(null); // clear this context's cached state
 * }), []);
 */
export function subscribeToProfileSwitch(
    handler: (detail: ProfileSwitchedEventDetail) => void,
): () => void {
    const listener = (e: Event) => {
        const ce = e as CustomEvent<ProfileSwitchedEventDetail>;
        handler(ce.detail);
    };
    window.addEventListener(PROFILE_SWITCHED_EVENT, listener);
    return () => window.removeEventListener(PROFILE_SWITCHED_EVENT, listener);
}
